import { ClearContactsAndCounts, SetPickedContactEmail, getDataForContactListTable } from '@/store/contacts/actions';
import { ListDeselect, ListsGetNamesStart, SingleListLoadStart } from '@/store/lists/actions';
import React, { useEffect, useState } from 'react';
import { SegmentDeselect, SingleSegmentLoadStart } from '@/store/segments/actions';
import { useDispatch, useSelector } from 'react-redux';

import { ApplicationState } from '@/store';
import DefaultContactAvatar from '@/images/avatar-contact.svg';
import { OverlayComponent } from '@/components/Overlay';
import { Portal } from '@material-ui/core';
import SingleContact from '@/components/Contacts/SingleContact';
import ViewEmail from '@/components/Contacts/ViewEmail';
import { pathsParams } from '@/services/dictionaries/Paths';
import { safelyDecodeBase64Url } from '@dev/ee-tools';
import { useHistoryBackActionContext } from '@/services/hooks/useHistoryBackAction';
import { useLocation } from 'react-router-dom';
import useMobile from '@/services/hooks/useMobile';
import { useParams } from 'react-router';
import { useFetchContactsBasedOnUrl } from '../../hooks/useFetchContactsBasedOnUrl';
import { useContactsPagination } from '../../hooks/useContactsPagination';
import { useContactQueryParams } from '../../hooks';
import { TopFiltersWithSearch } from '../TopFiltersWithSearch';
import { MobileContactTable } from './components/MobileContactTable';
import { DesktopContactTable } from './components/DesktopContactTable';
import { ContentVariantWrapper } from '../ContentVariantWrapper';
import { ContactTableContextProvider } from './context';

export const ContactListTable = () => {
    const isMobile = useMobile();
    const dispatch = useDispatch();
    const { listname, segmentname } = useParams<Record<Lowercase<keyof typeof pathsParams>, string>>();
    const { state } = useLocation<Record<keyof typeof pathsParams, string>>();
    const historyBackAction = useHistoryBackActionContext();

    const [contactOverlay, setContactOverlay] = useState<string>(
        !!state?.overlayContact && safelyDecodeBase64Url(state?.overlayContact)
    );
    const [emailDetails, setEmailDetails] = useState({ open: false, emailId: null });
    const { areAllListNamesLoaded } = useSelector((state: ApplicationState) =>
        getDataForContactListTable(state, listname || segmentname)
    );

    const { queryPage, queryPerPage } = useContactQueryParams();

    useContactsPagination();
    useFetchContactsBasedOnUrl();
    useEffect(() => {
        if (listname) {
            dispatch(SingleListLoadStart(safelyDecodeBase64Url(listname)));
        } else if (segmentname) {
            dispatch(SingleSegmentLoadStart(safelyDecodeBase64Url(segmentname)));
        }
        if (!areAllListNamesLoaded) {
            dispatch(ListsGetNamesStart());
        }

        !state?.overlayContact && setContactOverlay(null);
    }, [listname, segmentname]);

    useEffect(
        () => () => {
            dispatch(!!listname ? ListDeselect() : SegmentDeselect());
            dispatch(ClearContactsAndCounts());
            !!state?.overlayContact && setContactOverlay(safelyDecodeBase64Url(state?.overlayContact));
        },
        []
    );
    const openSingleContactOverlay = (email: string): void => {
        setContactOverlay(email);
        historyBackAction?.pushBackCallback(handleCloseOverlay);
    };

    const handleCloseOverlay = (): boolean => {
        dispatch(SetPickedContactEmail(null));
        setContactOverlay(null);

        return true;
    };

    const clickCloseButton = () => {
        historyBackAction.popAndExecuteBackCallback();
    };

    return (
        <>
            <ContentVariantWrapper>
                <TopFiltersWithSearch />
                <ContactTableContextProvider>
                    {isMobile ? (
                        <MobileContactTable
                            openSingleContactOverlay={openSingleContactOverlay}
                            activePage={queryPage}
                            perPage={queryPerPage}
                        />
                    ) : (
                        <DesktopContactTable
                            openSingleContactOverlay={openSingleContactOverlay}
                            activePage={queryPage}
                            perPage={queryPerPage}
                        />
                    )}
                </ContactTableContextProvider>
            </ContentVariantWrapper>

            {!!contactOverlay && (
                <OverlayComponent open={!!contactOverlay} onClose={clickCloseButton}>
                    <SingleContact
                        isHeaderHidden
                        contactEmail={contactOverlay}
                        onClickLogsVisibilityIcon={(email: string, msgId: string) =>
                            setEmailDetails({ open: true, emailId: msgId })
                        }
                        contactDefaultIcon={<DefaultContactAvatar />}
                    />
                </OverlayComponent>
            )}
            {emailDetails.open && (
                <Portal>
                    <ViewEmail
                        open={emailDetails.open}
                        msgId={emailDetails.emailId}
                        onClose={() => setEmailDetails({ open: false, emailId: null })}
                    />
                </Portal>
            )}
        </>
    );
};
